import React, { useState, useCallback, useEffect, useMemo, useContext, useRef } from 'react';
import { widgetBgColor, widgetBorderColor, widgetInnerPadding } from './ui-kit/utils';
import WidgetSettings from './WidgetSettings';

// Moved it back to the way these were being imported before because filters (and
// possibly other settings) were not working correctly. TODO: Investigate why this
// is the case and move back to lazy loading of the widgets.
import AnnouncementsWidget from './widgets/AnnouncementsWidget';
import IncidentStreetViewWidget from './widgets/IncidentStreetViewWidget';
import CrewAvailabilityWidget from './widgets/CrewAvailabilityWidget';
import CrewLevelWarnings from './widgets/CrewLevelWarnings';
import ResponseLevelPieChart from './widgets/ResponseLevelPieChartWidget';
import ApplianceList from './widgets/ApplianceList';
import Map from '../region_map/Map';
import ContactListWidget from './widgets/ContactListWidget';
import RidershipDetailsWidget from './widgets/RidershipDetailsWidget';
import LiveIncidentMap from './widgets/LiveIncidentMap';
import CrewConfirmations from './widgets/CrewConfirmations';
import IncidentMessage from './widgets/IncidentMessage';
import IncidentRadioChannels from './widgets/IncidentRadioChannels';
import IncidentNotes from './widgets/IncidentNotes';
import IncidentCrewingWarnings from './widgets/IncidentCrewingWarnings';
import ActiveIncidentList from './widgets/ActiveIncidentList';
import ContractCompliance from './widgets/ContractCompliance';
import IframeWidget from './widgets/IframeWidget';
import PersonalCalendarWidget from './widgets/PersonalCalendarWidget';
import DutyPlannerWidget from './widgets/DutyPlannerWidget';
import RecentActivityList from './widgets/RecentActivityList';
import QuickIncidentCreationButtons from './widgets/QuickIncidentCreationButtons';
import QuickAvailabilityChangeWidget from './widgets/QuickAvailabilityChangeWidget';
import IncidentSkillAssignments from './widgets/IncidentSkillAssignments';
import UserDutyTransactionsWidget from './widgets/UserDutyTransactionsWidget';
import P2000MessagesWidget from './widgets/P2000MessagesWidget';

import ErrorBoundary from '../general/ErrorBoundary';
import DashboardAPIManager from './DashboardAPIManager';
import { Card } from '../ui-kit';
import WidgetHeader from './WidgetHeader';
import FontSizeMultiplierContext from './FontSizeMultiplierContext';

// Auto-scrolling used to run one `setInterval` per widget and call
// `scrollTo({ behavior: 'smooth' })` on every tick. That stacks a new smooth
// scroll animation before the previous one finishes and, with several
// auto-scrolling widgets on a dashboard, several independent timers/animations
// running concurrently, which is expensive on low-power hardware such as a
// Raspberry Pi. A single shared heartbeat + direct `scrollTop` mutation is
// much cheaper.
const AUTO_SCROLL_TICK_MS = 400;
const autoScrollTickCallbacks = new Set();
let autoScrollHeartbeat = null;

const startAutoScrollHeartbeat = () => {
  if (autoScrollHeartbeat) return;
  autoScrollHeartbeat = setInterval(() => {
    for (const callback of autoScrollTickCallbacks) callback();
  }, AUTO_SCROLL_TICK_MS);
};

const stopAutoScrollHeartbeatIfIdle = () => {
  if (autoScrollTickCallbacks.size === 0 && autoScrollHeartbeat) {
    clearInterval(autoScrollHeartbeat);
    autoScrollHeartbeat = null;
  }
};

const WidgetImplementations = {
  region_map: Map,
  announcements: AnnouncementsWidget,
  response_level_pie_chart: ResponseLevelPieChart,
  appliance_list: ApplianceList,
  available_crew: CrewAvailabilityWidget,
  crew_level_warnings_v2: CrewLevelWarnings,
  contact_list: ContactListWidget,
  ridership_details: RidershipDetailsWidget,
  contract_compliance: ContractCompliance,
  live_incident_map: LiveIncidentMap,
  crew_confirmations: CrewConfirmations,
  incident_message: IncidentMessage,
  incident_crewing_warnings: IncidentCrewingWarnings,
  streetview: IncidentStreetViewWidget,
  active_incident_list: ActiveIncidentList,
  iframe: IframeWidget,
  recent_activity_list: RecentActivityList,
  incident_radio_channels: IncidentRadioChannels,
  incident_notes: IncidentNotes,
  personal_calendar: PersonalCalendarWidget,
  duty_planner: DutyPlannerWidget,
  incident_skill_assignments: IncidentSkillAssignments,
  quick_incident_creation_buttons: QuickIncidentCreationButtons,
  quick_availability_change: QuickAvailabilityChangeWidget,
  user_duty_transactions: UserDutyTransactionsWidget,
  p2000_messages: P2000MessagesWidget
};

const Widget = ({
  widgetData,
  editMode,
  dashboardGroupId,
  dashboardAccessScopeGroupId,
  singleWidgetMode,
  saveDashboard,
  removeWidget,
  enableDragging,
  updateWidgetTitle,
  updateWidgetHeight,
  updateWidgetSettings,
  setDisplayedIncident,
  locked,
  toggleLock
}) => {
  const fontSizeMultiplier = useContext(FontSizeMultiplierContext) || 100;

  let settings = useMemo(() => widgetData?.settings || {}, [widgetData]);
  const { id } = widgetData;
  const { defaultAutoScrollEnabled } = settings;
  const widgetContentId = `widget-${widgetData.archetype_id}-content-${id}`;

  let autoScrollEnabled =
    settings.autoScrollEnabled === undefined ? defaultAutoScrollEnabled : settings.autoScrollEnabled;

  // auto scroll is off when widget automatically resizes height
  if (settings.autoResizeHeight) {
    autoScrollEnabled = false;
    settings = { ...settings, autoScrollEnabled };
  }

  // if showHeader hasn't been set up, assume it is true by default. However, do
  // not set it in-place, because this is not allowed in strict mode
  let showHeader = settings.showHeader === undefined ? true : settings.showHeader;
  // show header always if we're in edit mode. If not, respect the widget
  // settings
  showHeader = !singleWidgetMode && (editMode || showHeader);

  const [showWidgetSettings, setShowWidgetSettings] = useState(false);

  // Reference to the scrollable widget content, cached once instead of
  // re-querying the DOM by id on every auto-scroll tick.
  const widgetScrollRef = useRef(null);
  // Only auto-scroll while the widget is actually visible on screen (and the
  // document/tab is visible), so hidden/off-screen widgets don't keep
  // animating in the background.
  const isVisibleRef = useRef(true);
  const scrollResetTimeoutRef = useRef(null);

  const tick = useCallback(() => {
    if (document.hidden || !isVisibleRef.current) return;

    const widget = widgetScrollRef.current;
    if (!widget) return;

    // to get the reset point, we take the full scrollable hight
    // and remove the current viewable height (offset)
    if (widget.scrollTop + widget.offsetHeight < widget.scrollHeight - 1) {
      // Mutate scrollTop directly instead of scrollTo({ behavior: 'smooth' }):
      // the smooth variant starts a new scroll animation on every tick, which
      // stacks up and is costly on low-power hardware.
      widget.scrollTo({ top: widget.scrollTop + 10, behavior: 'smooth' });
    } else {
      // When resetting the scroll, we stop the scrolling to allow some
      // time to read the bottom content. Then, scroll back to the beginning
      scrollResetTimeoutRef.current =
        scrollResetTimeoutRef.current || // prevent multiple timeouts stacking
        setTimeout(() => {
          widget.scrollTop = 0;
          scrollResetTimeoutRef.current = null;
        }, 2000);
    }
  }, []);

  const initializeScroll = () => {
    if (autoScrollTickCallbacks.has(tick)) return;
    autoScrollTickCallbacks.add(tick);
    startAutoScrollHeartbeat();
  };

  const stopScroll = () => {
    autoScrollTickCallbacks.delete(tick);
    if (scrollResetTimeoutRef.current) {
      clearTimeout(scrollResetTimeoutRef.current);
      scrollResetTimeoutRef.current = null;
    }
    stopAutoScrollHeartbeatIfIdle();
  };

  useEffect(() => {
    if (autoScrollEnabled) initializeScroll();
    return stopScroll;
  }, []);

  // Track visibility of the widget so auto-scroll pauses when it's scrolled
  // out of view (e.g. on a long dashboard) instead of continuing to run.
  useEffect(() => {
    const node = widgetScrollRef.current;
    if (!node || typeof IntersectionObserver === 'undefined') return;

    const observer = new IntersectionObserver(([entry]) => {
      isVisibleRef.current = entry.isIntersecting;
    });
    observer.observe(node);

    return () => observer.disconnect();
  }, [widgetScrollRef.current]);

  useEffect(() => {
    // This is necessary to turn on scroll if defaultAutoScrollEnabled is set to true.
    if (autoScrollEnabled) initializeScroll();
    else stopScroll();
  }, [autoScrollEnabled]);

  const toggleAutoScroll = useCallback(
    () => updateWidgetSettings(id, { autoScrollEnabled: !autoScrollEnabled }),
    [id, autoScrollEnabled]
  );

  const updateWidgetSettingsField = useCallback((field, value) => updateWidgetSettings(id, { [field]: value }), [id]);

  const toggleShowHeader = useCallback(
    () => updateWidgetSettings(id, { showHeader: !settings.showHeader }),
    [id, settings.showHeader]
  );

  const TagName = WidgetImplementations[widgetData.archetype_id];

  const SettingsTagName = TagName?.SettingsContent;
  const { title } = widgetData;
  const widgetSettings = useMemo(
    () => ({
      ...TagName?.defaultSettings,
      ...settings
    }),
    [TagName?.defaultSettings, settings]
  );

  const dashboardAPIManager = new DashboardAPIManager();

  const augmentedWidgetSettings = {
    ...widgetSettings,
    groupId: dashboardGroupId,
    accessScopeGroupId: dashboardAccessScopeGroupId,
    singleWidgetMode: singleWidgetMode,
    editMode: editMode,
    width: widgetData.width,
    widgetId: widgetData.id,
    dashboardAPIManager,
    setDisplayedIncident: setDisplayedIncident,
    incidentLocked: locked,
    toggleIncidentLock: toggleLock
  };

  // reference to the widget content to observe its size
  const widgetContentRef = useRef(null);
  const resizeObserver = useRef(null);

  useEffect(() => {
    if (!widgetContentRef.current) return;
    if (resizeObserver.current) resizeObserver.current.disconnect();

    const observer = new ResizeObserver(elements => {
      // don't mess with the height of the widget if we're in edit mode
      if (settings.autoResizeHeight && !editMode) {
        const element = elements[0];
        let height = element.target.offsetHeight + 18;
        if (showHeader) height += 38; // height of title is about 38 px
        const heightInRows = Math.ceil(height / 40);
        if (heightInRows > 1) updateWidgetHeight(id, heightInRows);
      }
    });
    observer.observe(widgetContentRef.current);
    resizeObserver.current = observer;

    return () => observer.disconnect();
  }, [widgetContentRef.current, editMode, settings.autoResizeHeight]);

  if (!TagName) return <h1>Widget does not exist {widgetData.archetype_id}</h1>;

  return (
    <Card
      className="h-100"
      style={{ background: `${widgetBgColor}`, borderColor: widgetBorderColor }}
      id={`widget-${widgetData.archetype_id}-${id}`}
    >
      {showHeader && (
        <WidgetHeader
          editMode={editMode}
          updateWidgetTitle={updateWidgetTitle}
          enableDragging={enableDragging}
          showHeader={settings.showHeader}
          toggleShowHeader={toggleShowHeader}
          removeWidget={removeWidget}
          autoScrollEnabled={autoScrollEnabled}
          toggleAutoScroll={toggleAutoScroll}
          setShowWidgetSettings={setShowWidgetSettings}
          SettingsTagName={SettingsTagName}
          title={title}
          id={id}
        />
      )}
      <Card.Body ref={widgetScrollRef} id={widgetContentId} className={`${widgetInnerPadding} overflow-auto h-100`}>
        {SettingsTagName && (
          <WidgetSettings show={showWidgetSettings} setShow={setShowWidgetSettings} saveSettings={saveDashboard}>
            <SettingsTagName
              // using widget is deprecated, but is used by many "old" class
              // component Settings. Use widgetSettings in combination with
              // updateWidgetSettingsField instead
              widget={widgetData}
              widgetArchetypeId={widgetData.archetype_id}
              widgetSettings={widgetSettings}
              groupId={dashboardGroupId}
              accessScopeGroupId={dashboardAccessScopeGroupId}
              updateWidgetSettingsField={updateWidgetSettingsField}
              autoScroll={autoScrollEnabled}
              dashboardAPIManager={dashboardAPIManager}
            />
          </WidgetSettings>
        )}
        <ErrorBoundary>
          <div style={{ height: '100%', fontSize: `${fontSizeMultiplier}%` }}>
            {settings.autoResizeHeight && (
              <div ref={widgetContentRef}>
                <TagName {...augmentedWidgetSettings} />
              </div>
            )}
            {settings.autoResizeHeight || <TagName {...augmentedWidgetSettings} />}
          </div>
        </ErrorBoundary>
      </Card.Body>
    </Card>
  );
};

export default Widget;
export { WidgetImplementations };
